import type { AnalyticsContext } from 'whoop-analytics';
import type { Product } from 'services/generated/commerce-service/models/Product';
import type { Variant } from 'services/generated/commerce-service/models/Variant';
import {
  getPriceValues,
  getSubscriptionType,
  getTrialDays,
  getVariantColor,
} from 'ui/utils/variantHelpers';
import type { ParsedCartItem, ParsedCartItemGroup } from 'ui/types/cartTypes';
import {
  ANNUAL_MEMBERSHIP_KEY,
  TRIAL_MEMBERSHIP_KEY,
  TWENTYFOUR_MONTH_MEMBERSHIP_KEY,
  MONTHLY_MEMBERSHIP_KEY,
} from 'ui/utils/constants/membershipConstants';
import { UpgradeMembershipOptionsUserProperty } from 'whoop-analytics/lib/types';
import { MembershipTier } from '../constants/maverick';
import { TRIAL_DEPRECATED } from '../constants/memberships';
import {
  getCartItemPriceValues,
  getCartItemsFromCartItemGroups,
} from '@/components/Cart/cart.utils';
import { PlanTypes } from 'ui/utils/constants/maverick';
import { getIsMembershipTypeInCart } from '@/lib/utils/membershipHelper';
import { FAMILY_MEMBERSHIP_KEY } from 'ui/utils/constants/familyConstants';

export const getAnalyticsAmount = (amountInCents: number): number => {
  return Number((amountInCents / 100).toFixed(2));
};

export const getNameFromVariant = (
  products: Product[],
  selectedVariant: Variant,
): string => {
  const selectedProduct = products.find((product) =>
    product.variants.some((variant) => variant.sku === selectedVariant.sku),
  );
  return selectedProduct?.name || '';
};

export const getItemProperties = (
  name: string,
  variant: Variant,
): AnalyticsContext => {
  const priceCents = getPriceValues(variant).displayPrice || 0;
  if (getVariantColor(variant) === 'Onyx') {
    return {
      sku: variant.sku,
      name: 'WHOOP 4.0',
      variant: 'Onyx',
      price: priceCents / 100,
    };
  }
  return {
    sku: variant.sku,
    name,
    price: priceCents / 100,
  };
};

export const getItemPropertiesV2 = (
  product: ParsedCartItem,
): AnalyticsContext => {
  const productDisplayPrice =
    product.discountedPrice?.value.centAmount ??
    product.originalPrice.centAmount;
  return {
    sku: product.id,
    name: product.title,
    price: getAnalyticsAmount(productDisplayPrice),
  };
};

export const getMembershipProperties = (
  membershipVariant: Variant,
  language: string,
): AnalyticsContext => {
  const name = membershipVariant.attributes.find(
    (attr) => attr.name === 'line-item-description',
  )?.value;
  const priceCents = getPriceValues(membershipVariant).displayPrice || 0;
  return {
    sku: membershipVariant.sku,
    name: name[language] || '',
    price: priceCents / 100,
    membership: getTrialDays(membershipVariant),
    is_trial: getSubscriptionType(membershipVariant) === TRIAL_DEPRECATED,
  };
};

export const getMembershipDays = (membership: ParsedCartItem): number => {
  const isTwoYear =
    membership.key === TWENTYFOUR_MONTH_MEMBERSHIP_KEY ||
    getIsMembershipTypeInCart(
      [{ title: '', cartItems: [membership] }],
      PlanTypes.TWO_YEAR,
    );

  const isAnnual =
    membership.key === ANNUAL_MEMBERSHIP_KEY ||
    getIsMembershipTypeInCart(
      [{ title: '', cartItems: [membership] }],
      PlanTypes.ANNUAL,
    );

  const isTrial =
    membership.key === TRIAL_MEMBERSHIP_KEY ||
    getIsMembershipTypeInCart(
      [{ title: '', cartItems: [membership] }],
      PlanTypes.TRIAL,
    );

  const isMonthly = membership.key === MONTHLY_MEMBERSHIP_KEY;

  const isFamily =
    membership.key === FAMILY_MEMBERSHIP_KEY ||
    getIsMembershipTypeInCart(
      [{ title: '', cartItems: [membership] }],
      PlanTypes.FAMILY,
    );

  switch (true) {
    case isTwoYear:
      return 24 * 30;
    case isTrial:
      return 30;
    case isMonthly:
      return 30;
    case isAnnual:
    case isFamily:
    default:
      return 12 * 30;
  }
};

export const getAccessoryContextV2 = (
  cartItemGroups: ParsedCartItemGroup[],
) => {
  const cartItems = getCartItemsFromCartItemGroups(cartItemGroups);

  const accessories = cartItems.filter(
    (product) =>
      product.productType !== 'membership' &&
      product.productType !== 'device' &&
      product.productType !== 'gen4-black-box-bp' &&
      product.productType !== 'extension' &&
      product.productType !== 'upcycle-kit' &&
      product.productType !== 'upcycle-membership' &&
      // dont include mcb free band, travel case, actual box, engraving
      !(
        product.productType === 'wrist-band' &&
        product.originalPrice.centAmount === 0
      ) &&
      product.productType !== 'mcb-engraving' &&
      product.productType !== 'mcb-case' &&
      product.productType !== 'milestone-collectors-box' &&
      product.productType !== 'upgrade-fee',
  );
  let uniqueAccessories = 0;
  let accessoryCount = 0;
  let accessoryTotal = 0;
  accessories.forEach((accessory) => {
    const { displayPrice } = getCartItemPriceValues(accessory);
    accessoryCount += accessory.quantity;
    accessoryTotal += displayPrice;
    uniqueAccessories += 1;
  });

  return {
    accessories,
    accessoryCount,
    accessoryTotal: getAnalyticsAmount(accessoryTotal),
    uniqueAccessories,
  };
};

export const getUpgradeMembershipOptions = (availableTiers) => {
  const availableTierNames = availableTiers.map((tier) => tier.name);
  let upgradeMembershipOptions =
    UpgradeMembershipOptionsUserProperty.OnePeakLife;
  if (
    availableTiers.length === 2 &&
    availableTierNames.includes(MembershipTier.PEAK) &&
    availableTierNames.includes(MembershipTier.LIFE)
  ) {
    upgradeMembershipOptions = UpgradeMembershipOptionsUserProperty.PeakLife;
  } else if (
    availableTiers.length === 2 &&
    availableTierNames.includes(MembershipTier.PEAK) &&
    availableTierNames.includes(MembershipTier.ONE)
  ) {
    upgradeMembershipOptions = UpgradeMembershipOptionsUserProperty.OnePeak;
  } else if (
    availableTiers.length === 1 &&
    availableTierNames.includes(MembershipTier.LIFE)
  ) {
    upgradeMembershipOptions = UpgradeMembershipOptionsUserProperty.Life;
  }

  return upgradeMembershipOptions;
};

export const getUpgradeFlowType = (billingType, isCanceled, billingStatus) => {
  let upgradeFlowType = 'other';
  if (billingType === 'FOUNDING_MEMBER') {
    upgradeFlowType = 'founding';
  } else if (billingType === null) {
    upgradeFlowType = 'elite';
  } else if (billingType === 'FAMILY_ADMIN') {
    upgradeFlowType = 'family admin';
  } else if (isCanceled) {
    upgradeFlowType = 'canceled';
  } else if (billingStatus === 'TRIALING') {
    upgradeFlowType = 'trialer';
  }

  return upgradeFlowType;
};
